import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { toast } from 'react-toastify';
import { usePixelMode, resolveEffectivePixelMode } from 'services/pixelMode';
import {
    authConfig,
    fetchOverview,
    hasLinkedSheet,
    iframeSnippetFor,
    integrationTitle,
    isSiteInstalled,
    pagesToCheck,
    plural,
    sheetTitle,
    timeAgo,
    TemplateOverview,
} from 'services/integrations';
import { IntegrationKind, KindLogo } from './integrationKinds';
import { IntegrationsTabs } from './pricing';

/**
 * "Mes intégrations" (`/integrations`): one card per site — the site name first, then the kind of
 * integration — plus one card per source without a site (API, and Google Sheet for accounts allowed to).
 * "Ajouter une intégration" opens the choice of integration type (`/integrations/start`), which is
 * also where a seller with nothing set up yet goes straight away.
 * See notes/ux-integrations-vendeur.md §4.5.
 */

type Tone = 'ok' | 'warn' | 'wait';

interface MenuItem {
    label: string;
    sub?: string;
    danger?: boolean;
    onClick: () => void;
}

interface CardModel {
    key: string;
    title: string;
    type: string;
    kind: IntegrationKind;
    states: { label: string; tone: Tone }[];
    stats: { label: string; value: string }[];
    chip?: { label: string; tone: 'ok' | 'warn' };
    actions: { label: string; onClick: () => void; primary?: boolean }[];
    // Always there: every integration can at least be deleted from its "⋯".
    menu: MenuItem[];
    needsAction: boolean;
}

const Dot = ({ tone }: { tone: Tone }) => (
    <span className={`h-2 w-2 shrink-0 rounded-full ${tone === 'ok' ? 'bg-[#1E9E52]' : tone === 'warn' ? 'bg-[#D9A100]' : 'bg-[#C9BFAE]'}`} />
);

const CardMenu = ({ items }: { items: MenuItem[] }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-label="Plus d'actions"
                className="rounded-[8px] border border-[#DDD5C9] bg-white px-2 py-[3px] font-hanken text-[14px] font-semibold leading-none text-[#3B352C] transition-colors hover:border-[#241C5C]"
            >
                ⋯
            </button>
            {open && (
                <div className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[240px] rounded-[12px] border border-[#EDE7DE] bg-white p-1.5 shadow-[0_14px_36px_rgba(20,17,14,0.14)]">
                    {items.map((item) => (
                        <button
                            key={item.label}
                            onClick={() => {
                                setOpen(false);
                                item.onClick();
                            }}
                            className="flex w-full flex-col items-start rounded-[8px] px-3 py-2 text-left transition-colors hover:bg-[#FAF8F4]"
                        >
                            <span className={`font-hanken text-[13.5px] font-semibold ${item.danger ? 'text-[#A8332B]' : 'text-[#1A1613]'}`}>{item.label}</span>
                            {item.sub && <span className="font-hanken text-[11.5px] text-[#8A8172]">{item.sub}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const Card = ({ card }: { card: CardModel }) => (
    <article
        className={`flex flex-col gap-2.5 rounded-[16px] border bg-white p-4 ${
            card.needsAction ? 'border-[#F0E4C4] shadow-[0_0_0_3px_#FCF9EF]' : 'border-[#EDE7DE]'
        }`}
    >
        <div className="flex items-start gap-2.5">
            <KindLogo kind={card.kind} size="sm" />
            <div className="min-w-0 flex-1">
                <h3 className="break-words font-newsreader text-[18px] font-medium leading-[1.15] text-[#14110E]">{card.title}</h3>
                <p className="mt-0.5 font-hanken text-[12px] text-[#6B6357]">{card.type}</p>
            </div>
            <CardMenu items={card.menu} />
        </div>
        {card.states.length > 0 && (
            <div className="flex flex-wrap gap-x-2.5 gap-y-1 font-hanken text-[12px] text-[#3B352C]">
                {card.states.map((s) => (
                    <span key={s.label} className="inline-flex items-center gap-1.5">
                        <Dot tone={s.tone} />
                        {s.label}
                    </span>
                ))}
            </div>
        )}
        {card.stats.length > 0 && (
            <dl className="flex flex-col gap-1 border-y border-[#F1ECE4] py-2">
                {card.stats.map((s) => (
                    <div key={s.label} className="flex items-baseline justify-between gap-2">
                        <dt className="font-hanken text-[11.5px] text-[#8A8172]">{s.label}</dt>
                        <dd className="text-right font-hanken text-[12.5px] font-semibold text-[#1A1613]">{s.value}</dd>
                    </div>
                ))}
            </dl>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-1.5">
            {card.chip && (
                <span
                    className={`rounded-full px-2 py-[2px] font-hanken text-[11.5px] font-semibold ${
                        card.chip.tone === 'ok' ? 'bg-[#D9F0DE] text-[#146334]' : 'bg-[#FCF2D4] text-[#7A5A05]'
                    }`}
                >
                    {card.chip.label}
                </span>
            )}
            <span className="ml-auto flex gap-1.5">
                {card.actions.map((a) => (
                    <button
                        key={a.label}
                        onClick={a.onClick}
                        className={
                            a.primary
                                ? 'rounded-[8px] bg-[#241C5C] px-2.5 py-[5px] font-hanken text-[12.5px] font-semibold text-white transition-colors hover:bg-[#332a75]'
                                : 'rounded-[8px] border border-[#DDD5C9] bg-white px-2.5 py-[5px] font-hanken text-[12.5px] font-semibold text-[#3B352C] transition-colors hover:border-[#241C5C]'
                        }
                    >
                        {a.label}
                    </button>
                ))}
            </span>
        </div>
    </article>
);

// The seller's API key also authenticates their AI agent (site-generation MCP): rotating or
// deleting it cuts both. Every risky action says exactly what stops working before it happens.
type ConfirmDelete = {
    title: string;
    body: string[];
    label: string;
    done: string;
    action: () => Promise<unknown>;
} | null;

type ApiDialog = { kind: 'regenerate' } | { kind: 'delete' } | { kind: 'newKey'; key: string } | null;

const Dialog = ({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(20,17,14,0.42)' }} onClick={onClose}>
        <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[480px] rounded-[18px] border border-[#EDE7DE] bg-white p-6 font-hanken shadow-[0_24px_60px_rgba(20,17,14,0.22)]"
        >
            <h2 className="font-newsreader text-[24px] font-medium leading-tight text-[#14110E]">{title}</h2>
            <div className="mt-3 flex flex-col gap-3 text-[14px] leading-relaxed text-[#3B352C]">{children}</div>
        </div>
    </div>
);

/* ── Loading: the page's own layout, with a soft light sweeping across it ── */

const SKELETON_STYLE = `
@keyframes leadrs-skeleton-sweep { from { background-position: 150% 0; } to { background-position: -50% 0; } }
.leadrs-skeleton {
    background: linear-gradient(90deg, #F1ECE4 0%, #F1ECE4 35%, #FAF7F2 50%, #F1ECE4 65%, #F1ECE4 100%);
    background-size: 220% 100%;
    animation: leadrs-skeleton-sweep 1.5s ease-in-out infinite;
    animation-delay: var(--skeleton-delay, 0ms);
}
@media (prefers-reduced-motion: reduce) { .leadrs-skeleton { animation: none; } }
`;

const Bone = ({ className = '' }: { className?: string }) => <span aria-hidden className={`leadrs-skeleton block rounded-[8px] ${className}`} />;

const SkeletonCard = ({ delay }: { delay: number }) => (
    <div className="flex flex-col gap-3 rounded-[16px] border border-[#EDE7DE] bg-white p-4" style={{ '--skeleton-delay': `${delay}ms` } as React.CSSProperties}>
        <div className="flex items-start gap-2.5">
            <Bone className="h-9 w-9 shrink-0 rounded-[10px]" />
            <div className="flex flex-1 flex-col gap-1.5 pt-0.5">
                <Bone className="h-[18px] w-3/4" />
                <Bone className="h-3 w-1/3" />
            </div>
            <Bone className="h-[22px] w-7 rounded-[8px]" />
        </div>
        <div className="flex gap-3">
            <Bone className="h-3 w-16" />
            <Bone className="h-3 w-24" />
        </div>
        <div className="flex flex-col gap-2 border-y border-[#F1ECE4] py-2.5">
            {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center justify-between">
                    <Bone className="h-2.5 w-24" />
                    <Bone className="h-2.5 w-10" />
                </div>
            ))}
        </div>
        <div className="flex items-center justify-between pt-0.5">
            <Bone className="h-5 w-28 rounded-full" />
            <Bone className="h-7 w-20 rounded-[8px]" />
        </div>
    </div>
);

const IntegrationsListSkeleton = () => (
    <div className="mx-auto flex max-w-[1140px] flex-col gap-6 pb-16" role="status" aria-live="polite" aria-busy="true">
        <style>{SKELETON_STYLE}</style>
        <span className="sr-only">Chargement de vos intégrations…</span>
        <div className="flex gap-6 border-b border-[#EDE7DE] pb-2.5 pt-2">
            <Bone className="h-4 w-32" />
            <Bone className="h-4 w-20" />
        </div>
        <div className="flex flex-wrap items-end justify-between gap-5">
            <div className="flex min-w-[240px] flex-1 flex-col gap-3">
                <Bone className="h-10 w-72 max-w-full rounded-[10px]" />
                <Bone className="h-4 w-96 max-w-full" />
            </div>
            <Bone className="h-12 w-52 max-w-full rounded-[12px]" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4].map((i) => (
                <SkeletonCard key={i} delay={i * 80} />
            ))}
            <div className="flex min-h-[170px] flex-col items-center justify-center gap-2 rounded-[16px] border border-dashed border-[#DDD5C9] bg-[#FAF8F4] p-4">
                <Bone className="h-9 w-9 rounded-full" />
                <Bone className="h-3.5 w-3/5" />
                <Bone className="h-3 w-4/5" />
            </div>
        </div>
    </div>
);

const IntegrationsList = () => {
    const navigate = useNavigate();
    const [overview, setOverview] = useState<TemplateOverview[]>([]);
    const [user, setUser] = useState<any>(null);
    const [apiKey, setApiKey] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const { globalMode } = usePixelMode();
    const pixelRequired = resolveEffectivePixelMode(globalMode, user) === 'mandatory';
    const canUseSheet = !!user?.canSellWithGSheet;

    useEffect(() => {
        Promise.all([
            fetchOverview().then(setOverview),
            axios.get(`${CONFIG.apiUrl}/user/me`, authConfig()).then((res) => setUser(res.data)),
            axios
                .get(`${CONFIG.apiUrl}/account/api-key`, authConfig())
                .then((res) => setApiKey(res.data?.apiKey || null))
                .catch(() => setApiKey(null)),
        ])
            .catch((e) => {
                console.error('Error loading integrations:', e);
                toast.error('Erreur lors du chargement des intégrations');
            })
            .finally(() => setLoading(false));
    }, []);


    const hasApiKey = apiKey && apiKey.status !== 'revoked';

    // Nothing set up at all: the choice of source is the only useful screen.
    useEffect(() => {
        if (!loading && overview.length === 0 && !hasApiKey) navigate('/integrations/start', { replace: true });
    }, [loading, overview.length, hasApiKey, navigate]);

    const openInstallation = (id: string, params = '') => navigate(`/integrations/${id}?tab=installation${params}`);

    // Every kind of integration (site, AI-built site, API, Google Sheet when allowed) is chosen,
    // with what it is and what it takes, on one page.
    const addIntegration = () => navigate('/integrations/start');

    const openSheet = async (templateId: string, googleSheetId: string | null) => {
        if (googleSheetId) {
            window.open(`https://docs.google.com/spreadsheets/d/${googleSheetId}/edit`, '_blank');
            return;
        }
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/form-template/${templateId}/get-or-create-sheet`, {}, authConfig());
            window.open(res.data.sheetUrl, '_blank');
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Erreur lors de l'ouverture du Google Sheet");
        }
    };

    // Every card can be deleted from its "⋯" menu, always behind a confirmation that says what
    // stops working (a deleted integration's form stops answering; a removed site's codes don't).
    const [confirm, setConfirm] = useState<ConfirmDelete>(null);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const runConfirm = async () => {
        if (!confirm) return;
        setConfirmBusy(true);
        try {
            await confirm.action();
            toast.success(confirm.done);
            setConfirm(null);
            setOverview(await fetchOverview());
        } catch (e: any) {
            toast.error(e?.response?.data?.message || e?.response?.data?.status || 'Erreur lors de la suppression');
        } finally {
            setConfirmBusy(false);
        }
    };
    const deleteTemplate = (templateId: string) => axios.delete(`${CONFIG.apiUrl}/form-template/${templateId}`, authConfig());
    const kept = 'Les leads déjà reçus restent dans Mes leads.';

    const askRemoveSite = (templateId: string, host: string) =>
        setConfirm({
            title: `Supprimer ${host} ?`,
            body: [
                `Le site disparaît de vos intégrations, avec ses pages et l'activité vérifiée de chacune. ${kept}`,
                `Important : tant que les codes Leadrs sont sur ${host}, son formulaire continue de recevoir des leads. Retirez-les de votre site.`,
            ],
            label: 'Supprimer ce site',
            done: `${host} retiré de vos intégrations`,
            action: () => axios.delete(`${CONFIG.apiUrl}/form-template/${templateId}/site`, { ...authConfig(), params: { hostname: host } }),
        });
    const askDeleteAiSite = (templateId: string, host: string) =>
        setConfirm({
            title: `Supprimer ${host} ?`,
            body: [
                `L'intégration de ce site créé avec l'IA est supprimée : son formulaire ne fonctionnera plus. Retirez-le du site, ou demandez-le à votre agent IA. ${kept}`,
            ],
            label: 'Supprimer cette intégration',
            done: `${host} supprimé de vos intégrations`,
            action: () => deleteTemplate(templateId),
        });
    const askDeleteApiTemplate = (templateId: string, name: string) =>
        setConfirm({
            title: `Supprimer « ${name} » ?`,
            body: [`Les leads envoyés à l'adresse de cette intégration seront refusés. ${kept}`],
            label: 'Supprimer cette intégration',
            done: 'Intégration supprimée',
            action: () => deleteTemplate(templateId),
        });
    const askDeleteSheet = (templateId: string, title: string) =>
        setConfirm({
            title: `Supprimer « ${title} » ?`,
            body: [
                `La synchronisation s'arrête : les nouvelles lignes du Google Sheet ne sont plus importées. ${kept}`,
                "Le fichier n'est pas effacé : vous pouvez le supprimer depuis votre Google Drive.",
            ],
            label: 'Supprimer cette intégration',
            done: 'Google Sheet supprimé de vos intégrations',
            action: () => deleteTemplate(templateId),
        });
    const askRemoveFormSheet = (templateId: string) =>
        setConfirm({
            title: 'Supprimer ce Google Sheet ?',
            body: [
                "Il n'est plus relié à votre formulaire : ses nouvelles lignes ne sont plus importées. Votre formulaire et vos sites ne changent pas.",
                "Le fichier n'est pas effacé : vous pouvez le supprimer depuis votre Google Drive.",
            ],
            label: 'Supprimer ce Google Sheet',
            done: 'Google Sheet supprimé de vos intégrations',
            action: () => axios.delete(`${CONFIG.apiUrl}/form-template/${templateId}/disable-sheet-sync`, authConfig()),
        });

    const [apiDialog, setApiDialog] = useState<ApiDialog>(null);
    const [apiBusy, setApiBusy] = useState(false);
    const [keyCopied, setKeyCopied] = useState(false);

    // Sites the AI agent built with this same key (they keep working, but the agent loses access).
    const aiSites = overview
        .filter((t) => t.source === 'mcp')
        .flatMap((t) => (t.sites.length ? t.sites.map((s) => s.hostname) : [t.name]));

    const copyApiKey = async () => {
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/account/api-key/reveal`, {}, authConfig());
            await navigator.clipboard?.writeText(res.data.apiKey);
            toast.success('Clé API copiée');
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Impossible de copier la clé');
        }
    };

    const regenerateApiKey = async () => {
        setApiBusy(true);
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/account/api-key/regenerate`, {}, authConfig());
            setApiKey((k: any) => ({ ...k, prefix: res.data.prefix, rotatedAt: new Date().toISOString(), lastUsedAt: null }));
            setKeyCopied(false);
            setApiDialog({ kind: 'newKey', key: res.data.apiKey });
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Erreur lors de la régénération de la clé');
        } finally {
            setApiBusy(false);
        }
    };

    const deleteApiKey = async () => {
        setApiBusy(true);
        try {
            await axios.delete(`${CONFIG.apiUrl}/account/api-key`, authConfig());
            setApiKey(null);
            setApiDialog(null);
            toast.success("Envoi par API supprimé : la clé n'est plus valable");
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Erreur lors de la suppression de la clé');
        } finally {
            setApiBusy(false);
        }
    };

    const cards = useMemo(() => {
        const out: CardModel[] = [];
        overview.forEach((t) => {
            if (t.isUniversal) {
                const fromAi = t.source === 'mcp';
                const type = fromAi ? "Site créé avec l'IA" : 'Formulaire Leadrs';
                const unpriced = t.activities.filter((a) => a.minimumPrice == null).length;
                // The seller's own form with no site yet (left by the former flow, which created it before
                // asking for a site): nothing to show — "Ajouter une intégration" reuses it once a site is given.
                if (t.sites.length === 0 && !fromAi && !t.totals.lastLeadAt) return;
                if (t.sites.length === 0) {
                    out.push({
                        key: t._id,
                        title: fromAi ? t.name : integrationTitle(t),
                        type: fromAi ? type : 'Pas encore installé sur un site',
                        kind: fromAi ? 'ai' : 'site',
                        states: [],
                        stats: [],
                        chip: { label: 'Installation à faire', tone: 'warn' },
                        actions: [{ label: 'Installer →', primary: true, onClick: () => openInstallation(t._id, '&step=install') }],
                        menu: [
                            {
                                label: 'Supprimer cette intégration',
                                danger: true,
                                onClick: () =>
                                    fromAi ? askDeleteAiSite(t._id, t.name) : askDeleteApiTemplate(t._id, integrationTitle(t)),
                            },
                        ],
                        needsAction: true,
                    });
                    return;
                }
                t.sites.forEach((s) => {
                    const installed = isSiteInstalled(s, pixelRequired);
                    const toCheck = pagesToCheck(s);
                    const states: CardModel['states'] = [];
                    if (fromAi) {
                        states.push({ label: installed ? 'En ligne' : 'Formulaire pas encore affiché', tone: installed ? 'ok' : 'warn' });
                    } else {
                        if (pixelRequired) states.push({ label: s.pixelDetected ? 'Suivi' : 'Suivi manquant', tone: s.pixelDetected ? 'ok' : 'warn' });
                        states.push({ label: s.iframeViews > 0 ? 'Formulaire' : 'Formulaire manquant', tone: s.iframeViews > 0 ? 'ok' : 'warn' });
                    }
                    const chip: CardModel['chip'] = !installed
                        ? { label: 'Installation à terminer', tone: 'warn' }
                        : unpriced > 0
                        ? { label: 'Prix à fixer', tone: 'warn' }
                        : toCheck > 0
                        ? { label: plural(toCheck, 'page à vérifier', 'pages à vérifier'), tone: 'warn' }
                        : { label: 'En vente', tone: 'ok' };
                    const actions: CardModel['actions'] = !installed
                        ? [{ label: 'Terminer →', primary: true, onClick: () => openInstallation(t._id, `&step=install&site=${encodeURIComponent(s.hostname)}`) }]
                        : [
                              ...(toCheck > 0 || unpriced > 0
                                  ? [
                                        unpriced > 0
                                            ? { label: 'Fixer les prix', onClick: () => navigate(`/integrations/${t._id}?tab=prix`) }
                                            : {
                                                  label: 'Vérifier',
                                                  onClick: () => navigate(`/integrations/${t._id}?tab=pages&site=${encodeURIComponent(s.hostname)}&filter=todo`),
                                              },
                                    ]
                                  : []),
                              { label: 'Ouvrir', primary: true, onClick: () => navigate(`/integrations/${t._id}`) },
                          ];
                    out.push({
                        key: `${t._id}:${s.hostname}`,
                        title: s.hostname,
                        type,
                        kind: fromAi ? 'ai' : 'site',
                        states,
                        stats: [
                            { label: 'Pages vérifiées', value: s.pages ? `${s.verifiedPages}/${s.pages}` : '—' },
                            { label: 'Leads 30 j', value: s.leads30 ? `${s.leads30}` : '—' },
                            { label: 'Dernier lead', value: timeAgo(s.lastLeadAt) },
                        ],
                        chip,
                        actions,
                        menu: [
                            { label: 'Ajouter une page', onClick: () => navigate(`/integrations/${t._id}?tab=pages&site=${encodeURIComponent(s.hostname)}&add=1`) },
                            { label: 'Mes pages', onClick: () => navigate(`/integrations/${t._id}?tab=pages&site=${encodeURIComponent(s.hostname)}`) },
                            { label: 'Ouvrir le site ↗', onClick: () => window.open(`https://${s.hostname}`, '_blank', 'noopener') },
                            ...(fromAi
                                ? []
                                : [
                                      {
                                          label: 'Copier le code du formulaire',
                                          onClick: () => {
                                              navigator.clipboard?.writeText(iframeSnippetFor(t._id)).catch(() => {});
                                              toast.success('Code du formulaire copié');
                                          },
                                      },
                                  ]),
                            { label: 'Performances', onClick: () => navigate(`/integrations/${t._id}?tab=performances`) },
                            fromAi
                                ? { label: 'Supprimer cette intégration', danger: true, onClick: () => askDeleteAiSite(t._id, s.hostname) }
                                : { label: 'Supprimer ce site', danger: true, onClick: () => askRemoveSite(t._id, s.hostname) },
                        ],
                        needsAction: !installed || unpriced > 0,
                    });
                });
            } else if (hasLinkedSheet(t)) {
                // Google Sheet import: an integration with a fixed activity and price, fed by its Sheet.
                out.push({
                    key: `${t._id}:sheet`,
                    // The product first (sub-industry, else industry), then the kind and the industry.
                    title: t.subIndustry || t.industry || 'Activité à définir',
                    type: t.industry ? `Google Sheet · ${t.industry}` : 'Google Sheet',
                    kind: 'sheet',
                    states: [{ label: 'Synchronisé', tone: 'ok' }],
                    stats: [
                        {
                            label: 'Prix minimum',
                            value:
                                t.minimumPrice == null
                                    ? '—'
                                    : t.accountPrice != null && t.accountPrice !== t.minimumPrice
                                    ? `${t.minimumPrice} € (compte : ${t.accountPrice} €)`
                                    : `${t.minimumPrice} €`,
                        },
                        { label: 'Leads 30 j', value: t.totals.leads30 ? `${t.totals.leads30}` : '—' },
                        { label: 'Dernier lead', value: timeAgo(t.totals.lastLeadAt) },
                    ],
                    chip: t.minimumPrice != null && t.industry ? { label: 'En vente', tone: 'ok' } : { label: 'Prix à fixer', tone: 'warn' },
                    actions: [
                        { label: 'Ouvrir le Sheet ↗', onClick: () => openSheet(t._id, t.googleSheetId) },
                        { label: 'Ouvrir', primary: true, onClick: () => navigate(`/integrations/${t._id}`) },
                    ],
                    menu: [
                        { label: "Modifier l'activité et le prix", onClick: () => navigate(`/integrations/${t._id}?tab=parametres`) },
                        { label: 'Supprimer cette intégration', sub: "Arrête l'import des nouvelles lignes", danger: true, onClick: () => askDeleteSheet(t._id, sheetTitle(t)) },
                    ],
                    needsAction: t.minimumPrice == null || !t.industry,
                });
            } else {
                // Older per-integration API (one webhook URL per template).
                out.push({
                    key: t._id,
                    title: t.name,
                    type: `Envoi par API${t.subIndustry ? ` · ${t.subIndustry}` : ''}`,
                    kind: 'api',
                    states: [],
                    stats: [
                        { label: 'Leads 30 j', value: t.totals.leads30 ? `${t.totals.leads30}` : '—' },
                        { label: 'Dernier lead', value: timeAgo(t.totals.lastLeadAt) },
                    ],
                    actions: [{ label: 'Ouvrir', primary: true, onClick: () => navigate(`/integrations/${t._id}`) }],
                    menu: [
                        { label: 'Performances', onClick: () => navigate(`/integrations/${t._id}?tab=performances`) },
                        { label: 'Supprimer cette intégration', danger: true, onClick: () => askDeleteApiTemplate(t._id, t.name) },
                    ],
                    needsAction: false,
                });
            }
            // A Sheet attached to "Mon formulaire Leadrs" (before the activity was asked first): its rows
            // have no page, hence no activity, and can't be sold. Say so and offer to redo it properly.
            if (canUseSheet && t.isUniversal && hasLinkedSheet(t) && t.source !== 'mcp') {
                out.push({
                    key: `${t._id}:sheet`,
                    title: 'Activité à définir',
                    type: 'Google Sheet · rattaché au formulaire',
                    kind: 'sheet',
                    states: [{ label: 'Activité non définie : les lignes ne sont pas vendues', tone: 'warn' }],
                    stats: [],
                    chip: { label: 'À recréer', tone: 'warn' },
                    actions: [{ label: 'Recréer avec une activité', primary: true, onClick: () => navigate('/integrations/new-sheet') }],
                    menu: [
                        { label: 'Ouvrir le Sheet ↗', onClick: () => openSheet(t._id, t.googleSheetId) },
                        { label: 'Supprimer ce Google Sheet', danger: true, onClick: () => askRemoveFormSheet(t._id) },
                    ],
                    needsAction: true,
                });
            }
        });
        if (hasApiKey) {
            out.push({
                key: 'api',
                title: 'Envoi par API',
                type: 'Depuis votre CRM ou vos outils',
                kind: 'api',
                states: [{ label: 'Clé active', tone: 'ok' }],
                stats: [
                    { label: 'Clé', value: apiKey.prefix ? `${apiKey.prefix}…` : '—' },
                    { label: 'Dernière utilisation', value: apiKey.lastUsedAt ? timeAgo(apiKey.lastUsedAt) : 'jamais' },
                    ...(aiSites.length ? [{ label: "Sert aussi à l'IA", value: plural(aiSites.length, 'site', 'sites') }] : []),
                ],
                actions: [{ label: 'Voir la doc', onClick: () => navigate('/seller-api-docs') }],
                menu: [
                    { label: 'Copier la clé', sub: 'Pour la coller dans votre outil', onClick: copyApiKey },
                    { label: 'Régénérer la clé', sub: "L'ancienne cesse de fonctionner", onClick: () => setApiDialog({ kind: 'regenerate' }) },
                    { label: "Supprimer l'envoi par API", sub: 'La clé est supprimée', danger: true, onClick: () => setApiDialog({ kind: 'delete' }) },
                ],
                needsAction: false,
            });
        }
        return out.sort((a, b) => Number(b.needsAction) - Number(a.needsAction));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overview, pixelRequired, canUseSheet, hasApiKey, apiKey, aiSites.length]);


    if (loading) return <IntegrationsListSkeleton />;

    return (
        <div className="mx-auto flex max-w-[1140px] flex-col gap-6 pb-16 font-hanken text-[#1A1613]">
            <div className="pt-2">
                <IntegrationsTabs
                    active="integrations"
                    unpriced={new Set(overview.flatMap((t) => t.activities.filter((a) => a.minimumPrice == null).map((a) => `${a.industry}|${a.subIndustry}`))).size}
                />
            </div>
            <header className="flex flex-wrap items-end justify-between gap-5">
                <div className="max-w-[620px]">
                    <h1 className="font-newsreader text-[40px] font-medium leading-[1.05] tracking-[-0.02em] text-[#14110E]">Mes intégrations</h1>
                    <p className="mt-3 font-hanken text-[15px] leading-relaxed text-[#6B6357]">
                        Les endroits d'où vos leads arrivent, et ce qu'il reste à faire pour les vendre.
                    </p>
                </div>
                <button
                    onClick={addIntegration}
                    className="rounded-[12px] bg-[#241C5C] px-[20px] py-[12px] font-hanken text-[14.5px] font-semibold text-white transition-colors hover:bg-[#332a75]"
                >
                    + Ajouter une intégration
                </button>
            </header>


            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {cards.map((card) => (
                    <Card key={card.key} card={card} />
                ))}
                <button
                    onClick={addIntegration}
                    className="flex min-h-[170px] flex-col items-center justify-center gap-1 rounded-[16px] border border-dashed border-[#DDD5C9] bg-[#FAF8F4] p-4 text-center transition-colors hover:border-[#241C5C]"
                >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#DDD5C9] bg-white text-[20px] text-[#241C5C]">+</span>
                    <strong className="font-hanken text-[13.5px] text-[#1A1613]">Ajouter une intégration</strong>
                    <span className="font-hanken text-[12px] text-[#8A8172]">
                        Un site, un site créé avec l'IA, l'envoi par API{canUseSheet ? ', un Google Sheet' : ''}
                    </span>
                </button>
            </div>

            {confirm && (
                <Dialog title={confirm.title} onClose={() => !confirmBusy && setConfirm(null)}>
                    {confirm.body.map((line) => (
                        <p key={line}>{line}</p>
                    ))}
                    <div className="mt-2 flex justify-end gap-2">
                        <button
                            onClick={() => setConfirm(null)}
                            disabled={confirmBusy}
                            className="rounded-[10px] border border-[#DDD5C9] bg-white px-4 py-2 text-[13.5px] font-semibold text-[#3B352C]"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={runConfirm}
                            disabled={confirmBusy}
                            className="rounded-[10px] bg-[#A8332B] px-4 py-2 text-[13.5px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                        >
                            {confirmBusy ? 'Suppression…' : confirm.label}
                        </button>
                    </div>
                </Dialog>
            )}

            {apiDialog?.kind === 'regenerate' && (
                <Dialog title="Régénérer la clé API ?" onClose={() => setApiDialog(null)}>
                    <p>
                        L'ancienne clé{apiKey?.prefix ? ` (${apiKey.prefix}…)` : ''} <strong>cesse de fonctionner immédiatement</strong>. Il faudra
                        coller la nouvelle :
                    </p>
                    <ul className="list-disc pl-5">
                        <li>
                            dans vos outils qui envoient des leads (CRM, Zapier, Make…)
                            {apiKey?.lastUsedAt ? ` : la clé a servi ${timeAgo(apiKey.lastUsedAt)}` : ''} ;
                        </li>
                        {aiSites.length > 0 && <li>dans votre agent IA, qui l'utilise pour {aiSites.join(', ')}.</li>}
                    </ul>
                    <p className="text-[13px] text-[#6B6357]">À faire si la clé a fuité, ou si vous ne savez plus où elle est utilisée.</p>
                    <div className="mt-2 flex justify-end gap-2">
                        <button onClick={() => setApiDialog(null)} className="rounded-[10px] border border-[#DDD5C9] bg-white px-4 py-2 text-[13.5px] font-semibold text-[#3B352C]">
                            Annuler
                        </button>
                        <button
                            onClick={regenerateApiKey}
                            disabled={apiBusy}
                            className="rounded-[10px] bg-[#241C5C] px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-[#332a75] disabled:opacity-50"
                        >
                            {apiBusy ? 'Régénération…' : 'Régénérer la clé'}
                        </button>
                    </div>
                </Dialog>
            )}

            {apiDialog?.kind === 'newKey' && (
                <Dialog title="Votre nouvelle clé API" onClose={() => setApiDialog(null)}>
                    <p>Copiez-la maintenant dans vos outils. Vous pourrez la retrouver avec « Copier la clé ».</p>
                    <div className="flex items-center gap-2 rounded-[11px] border border-[#EDE7DE] bg-[#F7F5F1] px-3 py-2.5">
                        <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-jbmono text-[12px]">{apiDialog.key}</code>
                        <button
                            onClick={() => {
                                navigator.clipboard?.writeText(apiDialog.key).catch(() => {});
                                setKeyCopied(true);
                            }}
                            className="shrink-0 rounded-[8px] bg-[#241C5C] px-3 py-1.5 text-[12.5px] font-semibold text-white"
                        >
                            {keyCopied ? 'Copiée ✓' : 'Copier'}
                        </button>
                    </div>
                    <div className="mt-2 flex justify-end">
                        <button onClick={() => setApiDialog(null)} className="rounded-[10px] bg-[#241C5C] px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-[#332a75]">
                            Terminé
                        </button>
                    </div>
                </Dialog>
            )}

            {apiDialog?.kind === 'delete' && (
                <Dialog title="Supprimer l'envoi par API ?" onClose={() => setApiDialog(null)}>
                    <p>
                        La clé est supprimée : <strong>tout lead envoyé avec elle sera refusé</strong>
                        {apiKey?.lastUsedAt ? ` (elle a servi ${timeAgo(apiKey.lastUsedAt)})` : ''}.
                    </p>
                    {aiSites.length > 0 && (
                        <p>
                            Votre agent IA ne pourra plus modifier {aiSites.join(', ')}. Les formulaires déjà en ligne sur ces sites continuent de
                            fonctionner.
                        </p>
                    )}
                    <p className="text-[13px] text-[#6B6357]">Vous pourrez créer une nouvelle clé à tout moment depuis « Ajouter une intégration ».</p>
                    <div className="mt-2 flex justify-end gap-2">
                        <button onClick={() => setApiDialog(null)} className="rounded-[10px] border border-[#DDD5C9] bg-white px-4 py-2 text-[13.5px] font-semibold text-[#3B352C]">
                            Annuler
                        </button>
                        <button
                            onClick={deleteApiKey}
                            disabled={apiBusy}
                            className="rounded-[10px] bg-[#A8332B] px-4 py-2 text-[13.5px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                        >
                            {apiBusy ? 'Suppression…' : 'Supprimer la clé'}
                        </button>
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default IntegrationsList;
